/**
 * QM Calculator Updater
 * Updates the QM value when variant changes or quantity updates
 */

(function () {
	// Set to false to disable console logs in production
	const DEBUG = false;

	class QMCalculatorUpdater {
		constructor() {
			this.qmValueElement = document.querySelector("#qm-value");
			this.qmValueCard = document.querySelector("#qm-value-card");

			// Exit if the QM value element isn't found
			if (!this.qmValueElement) return;

			this.baseQMValue = 0; // QM value for quantity 1

			// Subscribe to the theme's variantChange event
			// This is fired AFTER the section HTML is replaced by product-info.js
			// and includes the full variant object
			if (
				typeof subscribe !== "undefined" &&
				typeof PUB_SUB_EVENTS !== "undefined"
			) {
				this.variantChangeUnsubscriber = subscribe(
					PUB_SUB_EVENTS.variantChange,
					this.handleVariantChange.bind(this)
				);
				
				// Subscribe to quantity update events
				this.quantityUpdateUnsubscriber = subscribe(
					PUB_SUB_EVENTS.quantityUpdate,
					this.updateQMDisplay.bind(this)
				);
			}

			// Find the quantity input (use the same selector as product-info.js)
			this.quantityInput = document.querySelector(".quantity__input");

			// Listen for direct input changes
			if (this.quantityInput) {
				// 'input' event fires immediately when the value changes
				this.quantityInput.addEventListener(
					"input",
					this.updateQMDisplay.bind(this)
				);
				// 'change' event fires when the input loses focus
				this.quantityInput.addEventListener(
					"change",
					this.updateQMDisplay.bind(this)
				);
			}

			// Initialize with current variant
			this.initializeFromCurrentVariant();
		}

		disconnectedCallback() {
			// Clean up subscriptions when component is removed
			if (this.variantChangeUnsubscriber) {
				this.variantChangeUnsubscriber();
			}
			if (this.quantityUpdateUnsubscriber) {
				this.quantityUpdateUnsubscriber();
			}
		}

		handleVariantChange(event) {
			// This is called AFTER product-info.js replaces the section HTML
			// The variant object is passed directly in the event data
			if (DEBUG) {
				console.log(
					"lymo-qm: handleVariantChange from PUB_SUB_EVENTS.variantChange",
					event
				);
			}

			// Re-query DOM elements since the section HTML was replaced
			this.qmValueElement = document.querySelector("#qm-value");
			this.qmValueCard = document.querySelector("#qm-value-card");
			this.quantityInput = document.querySelector(".quantity__input");

			if (!this.qmValueElement) {
				if (DEBUG) {
					console.warn("lymo-qm: qm-value element not found after HTML replacement");
				}
				return;
			}

			const variant = event?.data?.variant;

			if (variant) {
				// Variant object is provided directly from the event - no need to query DOM!
				if (DEBUG) {
					console.log("lymo-qm: variant from event.data.variant", variant);
				}
				this.updateQMValueFromVariant(variant);
			} else {
				// Fallback: try to get from DOM (shouldn't normally happen)
				if (DEBUG) {
					console.warn("lymo-qm: variant not in event, falling back to DOM query");
				}
				this.initializeFromCurrentVariant();
			}
		}

		initializeFromCurrentVariant() {
			// Get the selected variant data from the DOM
			const variantData = this.findSelectedVariantData();
			if (variantData) {
				this.updateQMValueFromVariant(variantData);
			}
		}

		findSelectedVariantData() {
			// Selected variant data is stored in the data-selected-variant attribute
			const variantElement = document.querySelector("[data-selected-variant]");
			if (!variantElement?.textContent) {
				if (DEBUG) {
					console.warn("lymo-qm: Selected variant data not found.");
				}
				return null;
			}
			return JSON.parse(variantElement.textContent);
		}

		updateQMValueFromVariant(variant) {
			if (DEBUG) {
				console.log("lymo-qm: updateQMValueFromVariant --> variant", variant);
			}

			if (!variant) {
				this.baseQMValue = 0;
				this.hideQMCard();
				return;
			}

			// Check if variant has unit_price_measurement and if the unit is m2
			if (
				variant.unit_price_measurement &&
				variant.unit_price_measurement.quantity_unit === "m2"
			) {
				this.baseQMValue =
					parseFloat(variant.unit_price_measurement.quantity_value) || 0;
				if (DEBUG) {
					console.log(
						"lymo-qm: updateQMValueFromVariant --> baseQMValue",
						this.baseQMValue
					);
				}

				// Update the base value data attribute
				if (this.qmValueElement) {
					this.qmValueElement.dataset.baseValue = this.baseQMValue;
				}

				this.showQMCard();
				this.updateQMDisplay();
			} else {
				// Hide the QM card if the variant doesn't have m2 unit
				this.baseQMValue = 0;
				this.hideQMCard();
			}
		}

		getQuantityValue() {
			if (!this.quantityInput) return 1;

			// Get the input value and parse it
			const value = this.quantityInput.value.trim();
			if (value === "") return 1;

			const parsed = parseInt(value, 10);
			return isNaN(parsed) || parsed < 1 ? 1 : parsed;
		}

		updateQMDisplay() {
			if (!this.qmValueElement) return;

			// Get the current quantity
			const quantity = this.getQuantityValue();

			// Calculate total QM value
			const totalQMValue = this.baseQMValue * quantity;

			// Update the QM value display
			// Format the number to always show 2 decimal places and use comma as separator
			this.qmValueElement.textContent = totalQMValue
				.toFixed(2)
				.replace(".", ",");

			if (DEBUG) {
				console.log(
					"lymo-qm: updateQMDisplay --> totalQMValue",
					totalQMValue,
					"quantity",
					quantity
				);
			}
		}

		showQMCard() {
			if (this.qmValueCard) {
				this.qmValueCard.style.display = "";
			}
		}

		hideQMCard() {
			if (this.qmValueCard) {
				this.qmValueCard.style.display = "none";
			}
		}
	}

	// Initialize on DOMContentLoaded
	if (
		document.readyState === "complete" ||
		document.readyState === "interactive"
	) {
		new QMCalculatorUpdater();
	} else {
		document.addEventListener("DOMContentLoaded", () => {
			new QMCalculatorUpdater();
		});
	}

	// Handle Shopify Theme Editor updates or dynamic section loading
	document.addEventListener("shopify:section:load", () => {
		// Re-initialize if the relevant section is loaded/reloaded
		// Check if the elements exist before creating a new instance
		if (document.querySelector("#qm-value")) {
			new QMCalculatorUpdater();
		}
	});

	// Optional: Re-initialize for specific theme events like quick view opening
	// Example: document.addEventListener('theme:quick-view:open', () => new QMCalculatorUpdater());
})();
